/**
 * Per-blank Manage sheet — the single write anchor for one blank (UI refresh 2026-08,
 * docs/inventory-ui-refresh-plan-v1.md §3.3). Tiered for the shop floor: the two DAILY actions —
 * Take (walk-in / phone / sample draw-down → CONSUME) and Count (anchor to a physical count) — sit
 * always-visible with a live "New on hand" preview before every submit; the rare ones — Fix the
 * number (± ADJUST), Reorder level, Archive — live in a collapsed "More" drawer. On-hand is the
 * derived running sum of the ledger, never a stored counter, so each action posts a movement (or,
 * for the reorder level, a separate setting) and re-fetches.
 *
 * The old in-dialog "Add stock" card was REMOVED: the server recorded it as a plain ADJUST (history
 * then said "Adjusted", contradicting the button), a positive Fix-the-number covers it, S&S boxes
 * belong in Receiving, and bulk restock stays in the hub-level Add-stock dialog — which remains the
 * ONLY S&S-validated door for creating a brand-new blank.
 *
 * WRITES are admin / inventory-admin only (server-enforced via requireInventoryWrite). The controls
 * are disabled for a read-only viewer with a one-line explanation (cosmetic — the server 403 is the
 * real gate; `role.isInventoryWrite` mirrors it exactly). Movement rows that came from a print link
 * to the order that consumed the blank.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  BarChart3,
  ChevronDown,
  ClipboardList,
  Loader2,
  Package,
  Shirt,
} from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import {
  expectedShelf,
  NO_ROSE_ANSWERS,
  parseRefusal,
  pulledSummary,
  type Refusal,
  type RoseAnswers,
  sheetTag,
  shelfBody,
  shortSheets,
  unconfirmedLine,
  withRoseAnswer,
} from './count-shelf-lib';
import { blankLabel, invalidateInventory, variantLabel } from './inventory-lib';
import { orderCardSearch } from './order-detail-lib';

type BlankOnHand = components['schemas']['BlankOnHand'];
type BlankMovement = components['schemas']['BlankInventoryDetail']['movements'][number];
type BlankReorder = components['schemas']['BlankInventoryDetail']['reorder'];

/**
 * Optional S&S colorway image on the blank views — ships with the backend enrichment branch
 * (feat/blank-image-enrichment) whose types.gen regen carries it; until that merges the field is
 * simply absent and the header falls back to the icon tile. Documented additive-optional read only.
 */
const imageOf = (b: BlankOnHand | undefined): string | undefined =>
  (b as (BlankOnHand & { imageUrl?: string }) | undefined)?.imageUrl;

function fmtDate(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function BlankDetailDialog({
  blank,
  onClose,
}: {
  blank: BlankOnHand | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={blank != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] sm:max-w-lg">
        {blank && <ManageBody blank={blank} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function ManageBody({ blank, onClose }: { blank: BlankOnHand; onClose: () => void }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  const ssSku = blank.ssSku;
  // Cosmetic gate mirroring the server's requireInventoryWrite (admin || inventory-admin). The mutation
  // Alerts stay as the real 403 fallback; this only disables the affordances + explains why.
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;

  const detail = useQuery({
    queryKey: ['inventory', ssSku],
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/{ssSku}', {
        params: { path: { ssSku } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load blank.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const invalidate = () => invalidateInventory(qc);

  const onHand = detail.data?.onHand.onHand ?? blank.onHand;
  const lastCounted = detail.data?.onHand.lastCountedAt ?? blank.lastCountedAt;
  const variant = variantLabel(blank);
  const archived = detail.data?.archived ?? false;

  const image = imageOf(detail.data?.onHand as BlankOnHand | undefined) ?? imageOf(blank);

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-3">
          {image ? (
            <img
              src={image}
              alt=""
              loading="lazy"
              className="size-12 shrink-0 rounded-md border object-cover"
            />
          ) : (
            <span className="flex size-12 shrink-0 items-center justify-center rounded-md border text-muted-foreground">
              <Shirt className="size-6" />
            </span>
          )}
          <div className="min-w-0 text-left">
            <DialogTitle>{blankLabel(blank)}</DialogTitle>
            <DialogDescription>
              <span className="font-mono text-xs">{ssSku}</span>
              {variant && <> · {variant}</>}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-semibold tabular-nums">{onHand}</span>
        <span className="text-sm text-muted-foreground">on hand</span>
        {lastCounted ? (
          <Badge variant="outline">counted {fmtDate(lastCounted)}</Badge>
        ) : (
          <Badge variant="outline" className="border-amber-300 text-amber-700">
            never counted
          </Badge>
        )}
        {archived && (
          <Badge variant="outline" className="border-muted-foreground/40 text-muted-foreground">
            Archived
          </Badge>
        )}
      </div>

      {/* Pulled blanks (v2 B2): the book stays the big number; this is its split — what should be on
          the shelf and what the pull station recorded as off it — plus the one question and the leftover. */}
      <PulledSplit pulled={detail.data?.pulled} onHand={onHand} />

      {!canWrite && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          Read-only view. Ask an admin to make inventory changes.
        </p>
      )}

      {/* Below-reorder-level shortcut — the one cross-link with an action attached stays on top. */}
      {detail.data?.reorder.belowThreshold && (
        <Link
          to="/inventory/purchase-draft"
          onClick={onClose}
          className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted/50"
        >
          <BarChart3 className="size-4 shrink-0 text-red-600 dark:text-red-400" />
          <span>
            To reorder: buy up to {detail.data.reorder.effectiveThreshold} · suggested order{' '}
            <span className="font-semibold text-red-700 dark:text-red-400">
              {detail.data.reorder.suggestedOrderQty}
            </span>
          </span>
          <span className="ml-auto text-muted-foreground">→ draft</span>
        </Link>
      )}

      {/* Primary — the two daily actions, always visible, previewed before every submit. */}
      <ConsumeForm ssSku={ssSku} onHand={onHand} canWrite={canWrite} onDone={invalidate} />
      <CountForm
        ssSku={ssSku}
        onHand={onHand}
        canWrite={canWrite}
        onDone={invalidate}
        pulled={detail.data?.pulled}
      />

      {/* Secondary — the rare actions live behind one disclosure. */}
      <details className="group rounded-md border">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-sm font-medium text-muted-foreground">
          More — fix the number, reorder level, archive
          <ChevronDown className="ml-auto size-4 shrink-0 transition-transform group-open:rotate-180" />
        </summary>
        <div className="space-y-3 border-t p-3">
          <AdjustForm ssSku={ssSku} onHand={onHand} canWrite={canWrite} onDone={invalidate} />
          {detail.data && (
            <ParForm
              key={`par:${ssSku}:${detail.data.reorder.manualPar}`}
              ssSku={ssSku}
              reorder={detail.data.reorder}
              canWrite={canWrite}
              onDone={invalidate}
            />
          )}
          <ArchiveControl ssSku={ssSku} onHand={onHand} archived={archived} canWrite={canWrite} />
        </div>
      </details>

      <UsedBy ssSku={ssSku} onNavigate={onClose} />

      <Movements
        loading={detail.isPending}
        movements={detail.data?.movements ?? []}
        onNavigate={onClose}
      />
    </>
  );
}

type PulledBlock = NonNullable<components['schemas']['BlankInventoryDetail']['pulled']>;

/**
 * The book's split (pulled-blanks v2 §5 "Manage drawer"): *On the shelf ≈81 / Pulled 51 · #219 #220
 * #221*, the started-but-unconfirmed sheets (their stacks count as shelf until someone ticks them —
 * `/count` asks), a closed sheet's leftover, and a short recorded at the pull. Quiet when nothing is
 * pulled and nothing is asked. Absent block (old server / degraded read) → nothing.
 */
function PulledSplit({ pulled, onHand }: { pulled: PulledBlock | undefined; onHand: number }) {
  if (!pulled) return null;
  const shorts = shortSheets(pulled.sheets);
  if (
    pulled.now === 0 &&
    pulled.unconfirmed.length === 0 &&
    pulled.stillOut.length === 0 &&
    shorts.length === 0
  ) {
    return null;
  }
  return (
    <div className="space-y-1 rounded-md border px-3 py-2 text-xs" data-testid="pulled-split">
      <p>
        On the shelf ≈
        <span className="font-medium tabular-nums">{expectedShelf(onHand, pulled.now)}</span>
        {' · '}
        <span className="font-medium">{pulledSummary(pulled.sheets)}</span>
      </p>
      {shorts.map(({ sheet, short }) => (
        <p key={`short-${sheet.batchId}`} className="text-amber-700 dark:text-amber-400">
          short at the pull: got {sheet.recorded} of {sheet.onSheet} on {sheetTag(sheet)} ({short}{' '}
          short) — count this blank if nobody has.
        </p>
      ))}
      {pulled.unconfirmed.map((u) => (
        <p key={`unc-${u.batchId}`} className="text-amber-700 dark:text-amber-400">
          {unconfirmedLine(u)} — its stack counts as shelf until someone ticks it; the count asks.
        </p>
      ))}
      {pulled.stillOut.map((s) => (
        <p key={`out-${s.batchId}`} className="text-amber-700 dark:text-amber-400">
          {s.out} still out on closed {sheetTag(s)} — returned to the shelf? (the sheet's card)
        </p>
      ))}
    </div>
  );
}

/** Recent movements — 5 visible, the rest behind "Show all" (the old always-20 list dominated the
 *  phone scroll). */
function Movements({
  loading,
  movements,
  onNavigate,
}: {
  loading: boolean;
  movements: BlankMovement[];
  onNavigate: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  // "Show all" means ALL — the endpoint returns the complete ledger and it's already in memory; a
  // hidden tail under an "all" label reads as "that correction was never recorded" (review finding).
  const visible = showAll ? movements : movements.slice(0, 5);
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">Recent movements</h3>
      {loading && <Skeleton className="h-24 w-full" />}
      {!loading &&
        (movements.length === 0 ? (
          <p className="text-sm text-muted-foreground">No movements yet.</p>
        ) : (
          <>
            <MovementList movements={visible} onNavigate={onNavigate} />
            {!showAll && movements.length > 5 && (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Show all movements
              </button>
            )}
          </>
        ))}
    </div>
  );
}

/**
 * Archive / unarchive a blank (roadmap Phase 2c). Archiving retires a dead/typo'd blank from the working
 * views (the ledger is preserved) — the server refuses it (422) unless on-hand is 0 AND no open order
 * demands it, so the button is disabled until on-hand hits 0 (with a hint) and the server 422 is the real
 * guard. Unarchive restores it. Invalidates the whole inventory family so the lists update in place.
 */
function ArchiveControl({
  ssSku,
  onHand,
  archived,
  canWrite,
}: {
  ssSku: string;
  onHand: number;
  archived: boolean;
  canWrite: boolean;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      if (archived) {
        const { data, error } = await client.POST('/inventory/{ssSku}/unarchive', {
          params: { path: { ssSku } },
        });
        if (error || !data?.ok) throw new Error(error?.error?.detail ?? 'Unarchive failed.');
      } else {
        const { data, error } = await client.POST('/inventory/{ssSku}/archive', {
          params: { path: { ssSku } },
        });
        if (error || !data?.ok) throw new Error(error?.error?.detail ?? 'Archive failed.');
      }
    },
    onSuccess: () => {
      // Refetch this blank's detail (archived flag flips) + the family (it enters/leaves the lists).
      qc.invalidateQueries({ queryKey: ['inventory', ssSku] });
      invalidateInventory(qc);
    },
  });

  const blockedByStock = !archived && onHand !== 0;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border p-3">
      <Button
        variant="outline"
        size="sm"
        onClick={() => mutation.mutate()}
        disabled={!canWrite || blockedByStock || mutation.isPending}
        title={
          canWrite ? (blockedByStock ? 'Zero on-hand before archiving' : undefined) : 'Admins only'
        }
      >
        {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
        {archived ? 'Unarchive' : 'Archive'}
      </Button>
      <span className="text-xs text-muted-foreground">
        {archived
          ? 'Hidden from lists. Unarchive to restore.'
          : blockedByStock
            ? `Archive once on hand is 0 (has ${onHand} now). History is kept.`
            : 'Hide this blank from lists. History is kept — you can unarchive later.'}
      </span>
      {mutation.isError && (
        <p className="w-full text-xs text-destructive">
          {mutation.error instanceof Error ? mutation.error.message : 'Failed.'}
        </p>
      )}
    </div>
  );
}

/**
 * "Used by" cross-links (roadmap Phase 1b): the open orders demanding this blank + the catalog
 * products that use it (lazy-fetched from GET /inventory/:ssSku/usage when the drawer opens).
 * Orders/products are native <details> disclosures; each order deep-links to its job card. The
 * below-reorder-level draft shortcut moved up next to the on-hand header (it carries an action).
 */
function UsedBy({ ssSku, onNavigate }: { ssSku: string; onNavigate: () => void }) {
  const client = useApiClient();
  const usage = useQuery({
    queryKey: ['inventory-usage', ssSku],
    // The backend scans order lines + the catalog; fetch once per drawer-open, not on every window
    // refocus (the global QueryClient defaults to staleTime 0 + refetch-on-focus).
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/{ssSku}/usage', {
        params: { path: { ssSku } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load cross-links.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const orders = usage.data?.orders ?? [];
  const products = usage.data?.products ?? [];
  const demandPcs = usage.data?.demandPcs ?? 0;

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Used by</p>

      <JumpDisclosure
        loading={usage.isPending}
        error={usage.isError}
        icon={<ClipboardList className="size-4 shrink-0 text-primary" />}
        empty={orders.length === 0}
        summary={
          orders.length === 0 ? (
            'No open orders need this'
          ) : (
            <>
              <span className="font-semibold text-foreground">{demandPcs} pcs</span> demanded by{' '}
              {orders.length} open {orders.length === 1 ? 'order' : 'orders'}
            </>
          )
        }
      >
        {orders.map((o) => (
          <Link
            key={`${o.storeId}#${o.orderId}`}
            to="/orders/$storeId/$orderId"
            params={{ storeId: o.storeId, orderId: o.orderId }}
            search={orderCardSearch('inventory')}
            onClick={onNavigate}
            className="flex items-center justify-between gap-3 px-3 py-1.5 hover:bg-muted/50"
          >
            <span className="text-primary underline-offset-2 hover:underline">{o.orderName}</span>
            <span className="tabular-nums text-muted-foreground">{o.qty}</span>
          </Link>
        ))}
      </JumpDisclosure>

      <JumpDisclosure
        loading={usage.isPending}
        error={usage.isError}
        icon={<Package className="size-4 shrink-0 text-primary" />}
        empty={products.length === 0}
        summary={
          products.length === 0 ? (
            'Not on any product'
          ) : (
            <>
              Used by <span className="font-semibold text-foreground">{products.length}</span>{' '}
              {products.length === 1 ? 'product' : 'products'} in the catalog
            </>
          )
        }
      >
        {products.map((p) => (
          <div key={`${p.storeId}#${p.productId}`} className="px-3 py-1.5 text-muted-foreground">
            {p.title}
          </div>
        ))}
      </JumpDisclosure>
    </div>
  );
}

/** One "Jump to" row: a static muted line when loading/error/empty, else a native <details> disclosure. */
function JumpDisclosure({
  loading,
  error,
  icon,
  summary,
  empty,
  children,
}: {
  loading: boolean;
  error?: boolean;
  icon: ReactNode;
  summary: ReactNode;
  empty: boolean;
  children: ReactNode;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    );
  }
  // A failed fetch must read distinctly from a true empty result — never as an authoritative
  // "not demanded / not used" (an operator could act on that false negative).
  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-muted-foreground">
        {icon}
        <span>Couldn't load — reopen to retry.</span>
      </div>
    );
  }
  if (empty) {
    return (
      <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-muted-foreground">
        {icon}
        <span>{summary}</span>
      </div>
    );
  }
  return (
    <details className="group rounded-md border">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm">
        {icon}
        <span>{summary}</span>
        <ChevronDown className="ml-auto size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="divide-y border-t text-sm">{children}</div>
    </details>
  );
}

/**
 * The count-shelf numeric-input recipe (the app's phone-first reference): plain text + numeric
 * keypad, digits stripped on change, select-all on focus — kills the `e`/minus/spinner-drag failure
 * modes of type=number on shop-floor phones. `signed` admits ONE leading minus (the Fix-the-number
 * form). 44px tall — the touch-target floor.
 */
function QtyInput({
  id,
  value,
  onChange,
  placeholder,
  signed = false,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  signed?: boolean;
}) {
  return (
    <Input
      id={id}
      type="text"
      inputMode="numeric"
      enterKeyHint="done"
      autoComplete="off"
      placeholder={placeholder}
      value={value}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => {
        const raw = e.target.value;
        const cleaned = signed
          ? (raw.match(/^-?\d{0,4}/)?.[0] ?? '')
          : raw.replace(/\D/g, '').slice(0, 4);
        onChange(cleaned);
      }}
      // placeholder:font-normal is the structural half of the fix: the value is bold + centered, so a
      // placeholder inheriting that weight reads as a prefilled number however grey it is. Any hint
      // added here later is visibly a hint.
      className="h-11 w-24 text-center text-base font-semibold tabular-nums placeholder:font-normal"
    />
  );
}

/** "New on hand: N" — the live preview every submit shows before it's tapped; on an unguarded
 *  one-tap money write this is the confirm step. */
function NextOnHand({ next, delta }: { next: number | null; delta?: number | null }) {
  if (next === null) return <p className="h-4 text-xs" aria-hidden />;
  return (
    <p className="text-xs text-muted-foreground">
      New on hand: <span className="font-medium tabular-nums text-foreground">{next}</span>
      {delta != null && delta !== 0 && (
        <span className="tabular-nums"> ({delta > 0 ? `+${delta}` : delta})</span>
      )}
    </p>
  );
}

/**
 * "Take" — the manual CONSUME (walk-in / phone job / sample). Renamed from "Pull" (2026-08 refresh)
 * so "pull" keeps exactly one floor meaning: the physical press pull-sheet instruction, whose
 * consume fires automatically at press — recording a press-run pull here would double-subtract
 * (the documented mid-WIP hazard). The endpoint and semantics are unchanged.
 */
function ConsumeForm({
  ssSku,
  onHand,
  canWrite,
  onDone,
}: {
  ssSku: string;
  onHand: number;
  canWrite: boolean;
  onDone: () => void;
}) {
  const client = useApiClient();
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  // One idempotency token per action, minted on mount and rotated on success: a retry of the SAME
  // take reuses it (server dedupes → applied once); the NEXT distinct take gets a fresh one.
  const [token, setToken] = useState(() => crypto.randomUUID());
  const n = Number(qty);
  const valid = qty.trim() !== '' && Number.isInteger(n) && n > 0;

  const mutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST('/inventory/{ssSku}/consume', {
        params: { path: { ssSku } },
        body: { quantity: Math.trunc(n), reason: reason.trim() || undefined, clientToken: token },
      });
      if (error || !data?.ok) throw new Error(error?.error?.detail ?? 'Take failed.');
    },
    onSuccess: () => {
      setQty('');
      setReason('');
      setToken(crypto.randomUUID());
      onDone();
    },
  });

  return (
    <div className="space-y-2 rounded-md border p-3">
      <p className="text-sm font-medium">Take</p>
      <p className="text-xs text-muted-foreground">Walk-in, phone job, or sample.</p>
      {/* NOT exposition — this one prevents a real double-subtract (staff pull blanks per the press
          sheet, then come here and "record the pull", and the shirt comes off twice). It was the tail
          of a paragraph nobody finished; now it is a warning you can see. */}
      <p className="flex items-start gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
        <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
        Press-run pulls come out automatically — don't record them here.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor={`take-${ssSku}`} className="text-xs">
            Quantity
          </Label>
          <QtyInput id={`take-${ssSku}`} value={qty} onChange={setQty} />
        </div>
        <div className="min-w-[9rem] flex-1 space-y-1">
          <Label htmlFor={`take-rsn-${ssSku}`} className="text-xs">
            Note (optional)
          </Label>
          <Input
            id={`take-rsn-${ssSku}`}
            className="h-11"
            placeholder="walk-in, phone order…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <Button
          className="h-11"
          onClick={() => mutation.mutate()}
          disabled={!canWrite || !valid || mutation.isPending}
        >
          {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
          Take
        </Button>
      </div>
      <NextOnHand next={valid ? onHand - Math.trunc(n) : null} />
      {mutation.isError && (
        <p className="text-xs text-destructive">
          {mutation.error instanceof Error ? mutation.error.message : 'Take failed.'}
        </p>
      )}
    </div>
  );
}

/**
 * "Fix the number" — the ± ADJUST correction. The reason placeholder deliberately names ONLY
 * corrections (damaged / found extra / miscount): the old "walk-in job…" example invited the same
 * real event through two doors (a walk-in is a Take/CONSUME, never an ADJUST).
 */
function AdjustForm({
  ssSku,
  onHand,
  canWrite,
  onDone,
}: {
  ssSku: string;
  onHand: number;
  canWrite: boolean;
  onDone: () => void;
}) {
  const client = useApiClient();
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  // Idempotency token per adjust, rotated on success (see ConsumeForm) so a retry can't double-apply.
  const [token, setToken] = useState(() => crypto.randomUUID());
  const n = Number(qty);
  const valid = qty.trim() !== '' && Number.isFinite(n) && n !== 0 && reason.trim().length > 0;

  const mutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST('/inventory/{ssSku}/adjust', {
        params: { path: { ssSku } },
        body: { qtyDelta: Math.trunc(n), reason: reason.trim(), clientToken: token },
      });
      if (error || !data?.ok) throw new Error(error?.error?.detail ?? 'Adjustment failed.');
    },
    onSuccess: () => {
      setQty('');
      setReason('');
      setToken(crypto.randomUUID());
      onDone();
    },
  });

  return (
    <div className="space-y-2 rounded-md border p-3">
      <p className="text-sm font-medium">Fix the number (+/−)</p>
      <p className="text-xs text-muted-foreground">Reason required.</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor={`adj-${ssSku}`} className="text-xs">
            Change
          </Label>
          {/* The only numeric hint left in the sheet: this is the sole field that takes a negative,
              and nothing else on screen says so. A sign, not a sample quantity. */}
          <QtyInput id={`adj-${ssSku}`} value={qty} onChange={setQty} placeholder="+ / -" signed />
        </div>
        <div className="min-w-[9rem] flex-1 space-y-1">
          <Label htmlFor={`rsn-${ssSku}`} className="text-xs">
            Reason
          </Label>
          <Input
            id={`rsn-${ssSku}`}
            className="h-11"
            placeholder="damaged, found extra, miscount…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <Button
          className="h-11"
          variant="outline"
          onClick={() => mutation.mutate()}
          disabled={!canWrite || !valid || mutation.isPending}
        >
          {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
          Save adjustment
        </Button>
      </div>
      <NextOnHand
        next={valid ? onHand + Math.trunc(n) : null}
        delta={valid ? Math.trunc(n) : null}
      />
      {mutation.isError && (
        <p className="text-xs text-destructive">
          {mutation.error instanceof Error ? mutation.error.message : 'Adjustment failed.'}
        </p>
      )}
    </div>
  );
}

/**
 * Count — a SHELF count when the detail carries a pulled block (pulled-blanks v2 §2.8): the figure typed
 * is what is on the shelf, the server adds the recorded stacks (`book = shelf + Σ out`) and the preview
 * says so. The post carries only the typed figure plus advisory ids (`asOf`, the seen sheets) and, on a
 * `pulled_rose` resubmit, the answer. Without a block (old server / degraded read) it is the legacy
 * book count exactly as before. Every refusal is shown in the server's words and writes nothing.
 */
function CountForm({
  ssSku,
  onHand,
  canWrite,
  onDone,
  pulled,
}: {
  ssSku: string;
  onHand: number;
  canWrite: boolean;
  onDone: () => void;
  pulled?: PulledBlock;
}) {
  const client = useApiClient();
  const [counted, setCounted] = useState('');
  // Idempotency token per FIGURE (the observation), minted on first use and kept across a retry or a
  // `pulled_rose` resubmit (a refusal wrote nothing); the map is dropped after a success so the next
  // count is a new observation. A COUNT is naturally on-hand-idempotent, but the token also stops a
  // retry from appending a noise qtyDelta:0 row.
  const tokens = useRef(new Map<number, string>());
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [answers, setAnswers] = useState<RoseAnswers>(NO_ROSE_ANSWERS);
  const n = Number(counted);
  const valid = counted.trim() !== '' && Number.isInteger(n) && n >= 0;
  const figure = valid ? Math.trunc(n) : null;
  const now = pulled?.now ?? 0;

  const mutation = useMutation({
    mutationFn: async (roseAnswers: RoseAnswers) => {
      if (figure === null) return { kind: 'noop' as const };
      let token = tokens.current.get(figure);
      if (!token) {
        token = crypto.randomUUID();
        tokens.current.set(figure, token);
      }
      const res = await client.POST('/inventory/{ssSku}/count', {
        params: { path: { ssSku } },
        body: pulled
          ? shelfBody({
              shelfQty: figure,
              asOf: pulled.asOf,
              sheets: pulled.sheets,
              answers: roseAnswers,
              clientToken: token,
            })
          : { countedQty: figure, clientToken: token },
      });
      if (res.error) {
        return {
          kind: 'refused' as const,
          refusal: parseRefusal(res.error.error, res.response?.status),
        };
      }
      if (!res.data?.ok) throw new Error('Count failed.');
      return { kind: 'saved' as const };
    },
    onSuccess: (out) => {
      if (out.kind === 'refused') {
        setRefusal(out.refusal);
        return;
      }
      if (out.kind === 'noop') return;
      setCounted('');
      setRefusal(null);
      setAnswers(NO_ROSE_ANSWERS);
      tokens.current.clear();
      onDone();
    },
  });

  const answerRose = (sheetId: string, out: number, kind: 'counted' | 'press') => {
    const next = withRoseAnswer(answers, sheetId, out, kind);
    setAnswers(next);
    setRefusal(null);
    mutation.mutate(next);
  };

  return (
    <div className="space-y-2 rounded-md border p-3">
      <p className="text-sm font-medium">Count</p>
      <p className="text-xs text-muted-foreground">
        {pulled
          ? 'What is on the shelf right now — blanks recorded as pulled are added in.'
          : "What's on the shelf right now."}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor={`cnt-${ssSku}`} className="text-xs">
            {pulled ? 'On the shelf' : 'Counted'}
          </Label>
          <QtyInput
            id={`cnt-${ssSku}`}
            value={counted}
            onChange={(v) => {
              setCounted(v);
              setRefusal(null);
              // The answers were about the figure that was refused; a new figure starts clean.
              setAnswers(NO_ROSE_ANSWERS);
            }}
          />
        </div>
        <Button
          className="h-11"
          variant="outline"
          onClick={() => mutation.mutate(answers)}
          disabled={!canWrite || !valid || mutation.isPending}
        >
          {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
          Save count
        </Button>
      </div>
      <NextOnHand
        next={figure === null ? null : figure + now}
        delta={figure === null ? null : figure + now - onHand}
      />
      {pulled && figure !== null && now > 0 && (
        <p className="text-xs text-muted-foreground">
          {figure} on the shelf + {now} pulled.
        </p>
      )}
      {mutation.isError && (
        <p className="text-xs text-destructive">
          {mutation.error instanceof Error ? mutation.error.message : 'Count failed.'}
        </p>
      )}
      {refusal && (
        <div
          role="alert"
          data-testid="count-refusal"
          data-kind={refusal.kind}
          className="space-y-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-xs"
        >
          <p className="font-medium">{refusal.title}</p>
          <p>{refusal.detail} Nothing was written.</p>
          {refusal.kind === 'rose' && refusal.sheets[0] && (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={mutation.isPending}
                onClick={() =>
                  answerRose(
                    refusal.sheets[0]?.batchId ?? '',
                    refusal.sheets[0]?.out ?? 0,
                    'counted',
                  )
                }
              >
                Yes, I counted them
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={mutation.isPending}
                onClick={() =>
                  answerRose(refusal.sheets[0]?.batchId ?? '', refusal.sheets[0]?.out ?? 0, 'press')
                }
              >
                No, they were at the press
              </Button>
            </div>
          )}
          {(refusal.kind === 'changed' || refusal.kind === 'moved' || refusal.kind === 'stale') && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                // Re-read the blank (a fresh asOf and block); the figure stays for a second look.
                setRefusal(null);
                onDone();
              }}
            >
              Reload this blank
            </Button>
          )}
          {(refusal.kind === 'moving' || refusal.kind === 'other') && (
            <Button
              size="sm"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(answers)}
            >
              Save again
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Par editor (folded in from the Short & reorder page, roadmap Phase 1b). Setting/clearing the manual
 * reorder floor; an empty field means "clear" (par: null → falls back to the computed suggestion).
 * Seeded from the fetched reorder row and re-keyed by ManageBody on the refetched par, so a save →
 * invalidate → refetch re-seeds the input. Shows the effective threshold (manual par, else the
 * auto-suggested point) so the number's provenance is clear.
 */
function ParForm({
  ssSku,
  reorder,
  canWrite,
  onDone,
}: {
  ssSku: string;
  reorder: BlankReorder;
  canWrite: boolean;
  onDone: () => void;
}) {
  const client = useApiClient();
  const manualPar = reorder.manualPar;
  const [value, setValue] = useState(manualPar === null ? '' : String(manualPar));

  const save = useMutation({
    mutationFn: async (par: number | null) => {
      const { data, error } = await client.POST('/inventory/{ssSku}/par', {
        params: { path: { ssSku } },
        body: { par },
      });
      if (error || !data?.ok)
        throw new Error(error?.error?.detail ?? 'Failed to save the reorder level.');
    },
    onSuccess: () => onDone(),
  });

  const trimmed = value.trim();
  const n = Number(trimmed);
  const parsed = trimmed !== '' && Number.isInteger(n) && n >= 0 ? n : null;
  const dirty = (manualPar === null ? '' : String(manualPar)) !== trimmed;

  // The LEVEL is the buffer the engine actually added — `bufferTarget`, which on a par-less blank is
  // ceil(safetyStock) and NOT the displayed suggestedRop. Naming the wrong term here would invite the
  // operator to do arithmetic that doesn't work (rop 9 + committed 20 ≠ a target of 24). Falls back to
  // par/ROP against an older API, where `bufferTarget` is absent.
  const level = reorder.bufferTarget ?? manualPar ?? reorder.suggestedRop;
  const committed = reorder.committedDemand ?? 0;
  const provenance =
    manualPar !== null
      ? 'set by you'
      : reorder.bufferTarget != null
        ? 'safety stock, suggested from usage'
        : 'suggested from usage';
  const thresholdNote =
    reorder.effectiveThreshold === null
      ? 'No reorder level yet — set one, or keep using this blank and we’ll suggest one.'
      : level === null
        ? // A pure buy-to-order blank: no par, no usage history, bought only to cover what is on order.
          `No reorder level set — buying ${reorder.effectiveThreshold} purely to cover open orders.`
        : // Only claim the decomposition when it actually adds up: the engine floors the target at the
          // bare par/ROP, so on the rare row where that floor wins the two halves do not sum.
          `Reorder level ${level} (${provenance}) · buy up to ${reorder.effectiveThreshold}${
            committed > 0 && level + committed === reorder.effectiveThreshold
              ? ` (${committed} committed to open orders)`
              : ''
          }`;

  return (
    <div className="space-y-2 rounded-md border p-3">
      <p className="text-sm font-medium">Reorder level</p>
      <p className="text-xs text-muted-foreground">
        The shelf buffer to keep AFTER open orders are covered.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor={`par-${ssSku}`} className="text-xs">
            Reorder level
          </Label>
          <QtyInput id={`par-${ssSku}`} value={value} onChange={setValue} placeholder="—" />
        </div>
        <Button
          className="h-11"
          variant="outline"
          onClick={() => save.mutate(parsed)}
          disabled={!canWrite || save.isPending || (trimmed !== '' && parsed === null) || !dirty}
        >
          {save.isPending && <Loader2 className="size-4 animate-spin" />}
          Save
        </Button>
        {manualPar !== null && (
          <Badge variant="outline" className="text-[10px] font-normal">
            set
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{thresholdNote}</p>
      <p className="text-xs text-muted-foreground">Clear and Save to remove it.</p>
      {save.isError && (
        <p className="text-xs text-destructive">
          {save.error instanceof Error ? save.error.message : 'Save failed.'}
        </p>
      )}
    </div>
  );
}

const TYPE_LABEL: Record<BlankMovement['type'], string> = {
  RECEIVE: 'Received',
  CONSUME: 'Consumed',
  ADJUST: 'Adjusted',
  COUNT: 'Counted',
};

function MovementList({
  movements,
  onNavigate,
}: {
  movements: BlankMovement[];
  onNavigate: () => void;
}) {
  return (
    <ul className="divide-y rounded-md border text-sm">
      {movements.map((m) => (
        <li key={m.id} className="flex items-center justify-between gap-3 px-3 py-2">
          <div className="min-w-0">
            <span className="font-medium">{TYPE_LABEL[m.type]}</span>
            {m.type === 'COUNT' && m.basis === 'shelf' && (
              <span className="text-muted-foreground">
                {' '}
                — {m.shelfQty ?? 0} on the shelf + {m.pulledQty ?? 0} pulled
              </span>
            )}
            {m.reason && <span className="text-muted-foreground"> — {m.reason}</span>}
            {m.sourceInvoice && (
              <span className="text-muted-foreground"> · inv {m.sourceInvoice}</span>
            )}
            <div className="text-xs text-muted-foreground">
              {fmtDate(m.createdAt)}
              {m.orderRef && (
                <>
                  {' · '}
                  <Link
                    to="/orders/$storeId/$orderId"
                    params={{ storeId: m.orderRef.storeId, orderId: m.orderRef.orderId }}
                    search={orderCardSearch('inventory')}
                    onClick={onNavigate}
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    order ↗
                  </Link>
                </>
              )}
            </div>
          </div>
          <span
            className={
              m.qtyDelta < 0
                ? 'shrink-0 tabular-nums text-red-700 dark:text-red-400'
                : 'shrink-0 tabular-nums text-emerald-700 dark:text-emerald-400'
            }
          >
            {m.qtyDelta > 0 ? `+${m.qtyDelta}` : m.qtyDelta}
            {m.type === 'COUNT' && m.countedQty != null ? ` → ${m.countedQty}` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}
